

'use client';

import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger, PopoverAnchor } from '@/components/ui/popover';
import { MessageSquare, Phone } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { cn } from '@/doi-tac-module/lib/utils';
import { useState, useEffect } from 'react';
import { Job } from '@/doi-tac-module/lib/mock-data';
import { useLanguage } from '@/doi-tac-module/contexts/LanguageContext';
import { useAppTranslation } from '@/doi-tac-module/hooks/use-app-translation';
import { agencyTranslations } from '@/doi-tac-module/constants/agency-translations';


type ContactPerson = {
    id: string;
    name: string;
    avatarUrl: string;
    whatsapp?: string;
    phone?: string;
    messengerId?: string;
    facebookUrl?: string;
    zalo?: string;
    role?: string;
};

interface ContactButtonsProps {
    contact: ContactPerson;
    job?: Job; // Make job optional
    showChatText?: boolean; // New prop to control text visibility
    chatText?: string; // Explicit text for the chat button
    id?: string; // Custom ID for the container
}

export function ContactButtons({ contact, job, showChatText = false, chatText, id = "agencyContactButtons" }: ContactButtonsProps) {
    const { language } = useLanguage();
    const [isClient, setIsClient] = useState(false);
    const { t } = useAppTranslation();

    useEffect(() => {
        setIsClient(true);
    }, []);


    const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.stopPropagation(); // Prevent card's onClick from firing
    };

    const formatZaloLink = (zalo: string | undefined, phone: string | undefined) => {
        const number = (zalo || phone || '').replace(/\D/g, '');
        return number ? `https://zalo.me/${number}` : '#';
    };

    const formatFacebookLink = (facebookUrl: string | undefined, messengerId: string | undefined) => {
        if (facebookUrl) {
            if (facebookUrl.startsWith('http')) return facebookUrl;
            return `https://facebook.com/${facebookUrl}`;
        }
        if (messengerId && messengerId !== 'your_user_id') {
            if (messengerId.startsWith('http')) return messengerId;
            return `https://facebook.com/${messengerId}`;
        }
        return '#';
    };

    const zaloLink = formatZaloLink(contact.zalo, contact.phone);
    const facebookLink = formatFacebookLink(contact.facebookUrl, contact.messengerId);
    const phoneLink = contact.phone ? `tel:${contact.phone.replace(/\s/g, '')}` : '#';

    if (!isClient) {
        return null;
    }

    const resolvedChatText = chatText || (
        contact.role === 'MASTER_CTV'
            ? (agencyTranslations.candidateSearch as any).card.buttons.contactMaster[language]
            : (agencyTranslations.candidateSearch as any).card.buttons.contactAgency[language]
    );

    return (
        <>
            {/* Desktop Buttons */}
            <div id={id} className="hidden md:flex items-center gap-1">
                {showChatText ? (
                    <Button variant="outline" className="h-8 border-primary text-primary hover:bg-primary/5 cursor-not-allowed opacity-70 gap-2 px-3">
                        <MessageSquare className="h-4 w-4" />
                        <span className="text-xs font-bold whitespace-nowrap">{resolvedChatText}</span>
                    </Button>
                ) : (
                    <Button variant="outline" size="icon" className="h-8 w-8 border-primary text-primary hover:bg-primary/5 cursor-not-allowed opacity-70">
                        <MessageSquare className="h-4 w-4" />
                    </Button>
                )}
                <Button asChild variant="outline" size="icon" className={cn("h-8 w-8 border-blue-500 text-blue-500 hover:bg-blue-50 hover:text-blue-600", facebookLink === '#' && "opacity-50 pointer-events-none")}>
                    <Link href={facebookLink} target="_blank" onClick={handleLinkClick}>
                        <Image src="/img/BANGLADESH/TRANGDOITAC/Facebook.svg" alt="Facebook" width={20} height={20} />
                    </Link>
                </Button>
                <Button asChild variant="outline" size="icon" className={cn("h-8 w-8 border-[#0068FF] text-[#0068FF] hover:bg-blue-50 hover:text-[#0068FF]", zaloLink === '#' && "opacity-50 pointer-events-none")}>
                    <Link href={zaloLink} target="_blank" onClick={handleLinkClick}>
                        <Image src="/img/Zalo.svg" alt="Zalo" width={20} height={20} />
                    </Link>
                </Button>
                <Button asChild variant="outline" size="icon" className={cn("h-8 w-8 border-green-500 text-green-500 hover:bg-green-50 hover:text-green-600", phoneLink === '#' && "opacity-50 pointer-events-none")}>
                    <Link href={phoneLink} onClick={handleLinkClick}>
                        <Image src="/img/phone.svg" alt="Phone" width={20} height={20} />
                    </Link>
                </Button>
            </div>

            {/* Mobile Buttons */}
            <div className="md:hidden">
                <Popover>
                    <PopoverAnchor asChild>
                        <PopoverTrigger asChild>
                            <div id={id} className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                <Button size="icon" variant="outline" className={cn("h-8 w-8 border-blue-500 hover:bg-blue-50 flex-shrink-0 text-blue-500", facebookLink === '#' && "opacity-50 pointer-events-none")}>
                                    <Link href={facebookLink} target="_blank" onClick={handleLinkClick}>
                                        <Image src="/img/BANGLADESH/TRANGDOITAC/Facebook.svg" alt="Facebook" width={18} height={18} />
                                    </Link>
                                </Button>
                                <Button size="icon" variant="outline" className={cn("h-8 w-8 border-[#0068FF] hover:bg-blue-50 flex-shrink-0 text-[#0068FF]", zaloLink === '#' && "opacity-50 pointer-events-none")}>
                                    <Link href={zaloLink} target="_blank" onClick={handleLinkClick}>
                                        <Image src="/img/Zalo.svg" alt="Zalo" width={18} height={18} />
                                    </Link>
                                </Button>
                                <Button size="icon" variant="outline" className={cn("h-8 w-8 border-green-500 hover:bg-green-50 flex-shrink-0 text-green-500", phoneLink === '#' && "opacity-50 pointer-events-none")}>
                                    <Link href={phoneLink} onClick={handleLinkClick}>
                                        <Image src="/img/phone.svg" alt="Phone" width={18} height={18} />
                                    </Link>
                                </Button>
                                <Button size="icon" variant="outline" className="h-8 w-8 border-primary text-primary hover:bg-primary/5 cursor-not-allowed opacity-70">
                                    <MessageSquare className="h-4 w-4" />
                                </Button>
                            </div>
                        </PopoverTrigger>
                    </PopoverAnchor>
                    <PopoverContent id="LIENHETOMOBILE01" className="w-auto p-2" onClick={(e) => e.stopPropagation()}>
                        <div className="flex gap-2">
                            {showChatText ? (
                                <Button variant="outline" className="h-16 border-primary text-primary hover:bg-primary/5 cursor-not-allowed opacity-70 gap-2 px-4">
                                    <MessageSquare className="h-8 w-8" />
                                    <span className="font-bold whitespace-nowrap">{resolvedChatText}</span>
                                </Button>
                            ) : (
                                <Button variant="outline" size="icon" className="h-16 w-16 border-primary text-primary hover:bg-primary/5 cursor-not-allowed opacity-70">
                                    <MessageSquare className="h-8 w-8" />
                                </Button>
                            )}
                            <Button asChild variant="outline" size="icon" className={cn("h-16 w-16 border-blue-500 hover:bg-blue-50", facebookLink === '#' && "opacity-50 pointer-events-none")}>
                                <Link href={facebookLink} target="_blank" onClick={handleLinkClick}>
                                    <Image src="/img/BANGLADESH/TRANGDOITAC/Facebook.svg" alt="Facebook" width={32} height={32} />
                                </Link>
                            </Button>
                            <Button asChild variant="outline" size="icon" className={cn("h-16 w-16 border-[#0068FF] hover:bg-blue-50", zaloLink === '#' && "opacity-50 pointer-events-none")}>
                                <Link href={zaloLink} target="_blank" onClick={handleLinkClick}>
                                    <Image src="/img/Zalo.svg" alt="Zalo" width={32} height={32} />
                                </Link>
                            </Button>
                            <Button asChild variant="outline" size="icon" className={cn("h-16 w-16 border-green-500 hover:bg-green-50", phoneLink === '#' && "opacity-50 pointer-events-none")}>
                                <Link href={phoneLink} onClick={handleLinkClick}>
                                    <Image src="/img/phone.svg" alt="Phone" width={32} height={32} />
                                </Link>
                            </Button>
                        </div>
                    </PopoverContent>
                </Popover>
            </div>
        </>
    );
}

